import { Link } from '@tanstack/react-router';
import { T, Var } from 'gt-react';
import { formatDistanceToNow } from 'date-fns';
import {
  CheckCircleIcon,
  CircleDashedIcon,
  ChatTextIcon,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import type { CollectionThreadDto } from '@erp/rpc';
import { TYPE_LABELS, RESOLUTION_LABELS } from './threadLabels';

interface ThreadRowProps {
  thread: CollectionThreadDto;
  /** Defaults to the thread's own customer; pass to override for navigation. */
  customerId?: string;
  /** Shown on cross-customer lists (summary card, Collections Threads tab). */
  customerName?: string | null;
  /**
   * Tags the detail link with where it was opened from, which turns on the
   * detail page's "Back" link (to that origin) and Previous/Next navigation.
   * Omit on the per-customer panel.
   */
  linkFrom?: 'summary' | 'threads';
}

/**
 * One collection-thread list item. Reused by the per-customer panel and the
 * cross-customer lists (AR Summary card + Collections Threads tab). The latter
 * pass `customerName` to surface context the panel omits, and `linkFrom` to
 * enable origin-aware navigation on the detail page.
 */
export function ThreadRow({
  thread,
  customerId,
  customerName,
  linkFrom,
}: ThreadRowProps) {
  const isResolved = thread.status === 'resolved';
  const ageSource =
    isResolved && thread.resolvedAt ? thread.resolvedAt : thread.createdAt;
  const relative = formatDistanceToNow(new Date(ageSource), {
    addSuffix: true,
  });
  return (
    <Link
      to="/accounts-receivable/collections/$customerId/threads/$threadId"
      params={{
        customerId: customerId ?? thread.customerId,
        threadId: thread.id,
      }}
      search={linkFrom ? { from: linkFrom } : undefined}
      className="w-full text-left flex items-start gap-3 rounded border px-3 py-2 hover:bg-muted/40 transition-colors"
    >
      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center mt-0.5">
        {isResolved ? (
          <CheckCircleIcon className="h-4 w-4 text-green-600" />
        ) : (
          <CircleDashedIcon className="h-4 w-4 text-amber-600" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-muted-foreground shrink-0">
            {thread.referenceCode}
          </span>
          <span className="text-sm font-medium truncate">{thread.title}</span>
          <Badge variant="secondary" className="text-xs">
            {TYPE_LABELS[thread.type]}
          </Badge>
          {isResolved && thread.resolution && (
            <Badge variant="outline" className="text-xs">
              {RESOLUTION_LABELS[thread.resolution]}
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
          {customerName != null && (
            <span className="font-medium text-foreground/80 truncate">
              {customerName}
            </span>
          )}
          <span>
            {isResolved ? (
              <T>
                resolved <Var>{relative}</Var>
              </T>
            ) : (
              <T>
                opened <Var>{relative}</Var>
              </T>
            )}
          </span>
          {thread.description && (
            <span className="flex items-center gap-1">
              <ChatTextIcon className="h-3 w-3" />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
